import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { MappingContext } from '../Contexts/MappingContext';
import Select from 'react-select';
import debounce from 'lodash/debounce';

type AttributeValueContentProps = {
    category: MappingCategory;
    pair: AttributeValuePair;
    pairIndex: number;
};

export const AttributeValueContent = ({
    category,
    pair,
    pairIndex
}: AttributeValueContentProps) => {
    const count = category.attribute.length;
    const {
        updateAttributePair,
        isEditMode,
        getCategoryDropdownAttributeValues,
    } = useContext(MappingContext);

    const [filteredAttributeValueOptions, setFilteredAttributeValueOptions] = useState<{ value: string; label: string }[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);

    // Load attribute values based on search and page
    useEffect(() => {
        if (!pair.attribute || !isEditMode) {
            setFilteredAttributeValueOptions([]);
            setHasMore(false);
            return;
        }

        const loadAttributeValues = async () => {
            const result = await getCategoryDropdownAttributeValues(pair.attribute, page, searchValue);

            // Merge with existing selected value if it exists and not in the list (only on page 1)
            let options = result.items;
            if (page === 1 && pair.attributeValue && !options.find(opt => opt.value === pair.attributeValue)) {
                options = [{ value: pair.attributeValue, label: pair.attributeValue }, ...options];
            }

            // For page 1, replace options; for subsequent pages, append
            setFilteredAttributeValueOptions(prevOptions => {
                if (page === 1) {
                    return options;
                } else {
                    // Append new options, avoiding duplicates
                    const existingValues = new Set(prevOptions.map(opt => opt.value));
                    const newOptions = options.filter(opt => !existingValues.has(opt.value));
                    return [...prevOptions, ...newOptions];
                }
            });
            setHasMore(result.hasMore);
        };

        loadAttributeValues();
    }, [pair.attribute, isEditMode, getCategoryDropdownAttributeValues, pair.attributeValue, page, searchValue]);

    // Debounced search handler
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchValue(value);
            setPage(1);
        }, 500),
        []
    );

    // Handle input change
    const handleInputChange = (input: string) => {
        if (input === '') {
            setSearchValue('');
            setPage(1);
        } else {
            debouncedSearch(input);
        }
    };

    // Handle infinite scroll
    const handleMenuScrollToBottom = async () => {
        if (!hasMore || isLoadingMore || !pair.attribute) return;

        setIsLoadingMore(true);
        try {
            setPage(prevPage => prevPage + 1);
        } finally {
            setIsLoadingMore(false);
        }
    };

    // Values already paired with this attribute in OTHER rows of this category
    // — hide them from this row's dropdown so the reviewer can't create a
    // duplicate (attribute, value) pair. The current row's own value stays
    // visible so the selection doesn't appear blank after filtering.
    const takenValuesForThisAttribute = useMemo(() => {
        if (!pair.attribute) return new Set<string>();
        return new Set(
            (category.attribute || [])
                .filter((p, i) => i !== pairIndex && p.attribute === pair.attribute && p.attributeValue)
                .map((p) => p.attributeValue),
        );
    }, [category.attribute, pair.attribute, pairIndex]);

    const availableAttributeValueOptions = useMemo(
        () => filteredAttributeValueOptions.filter(
            (opt) => !takenValuesForThisAttribute.has(opt.value) || opt.value === pair.attributeValue,
        ),
        [filteredAttributeValueOptions, takenValuesForThisAttribute, pair.attributeValue],
    );

    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-relative">
            {/* Attribute Value Dropdown */}
            <div className="tw-flex-1 tw-min-w-0">
                {isEditMode ? (
                    <Select
                        options={availableAttributeValueOptions}
                        value={availableAttributeValueOptions.find(opt => opt.value === pair.attributeValue) || null}
                        onChange={(selectedOption) => updateAttributePair(category.id, pairIndex, { attributeValue: selectedOption?.value || '' })}
                        placeholder="Select"
                        isClearable={true}
                        maxMenuHeight={220}
                        menuPlacement="auto"
                        // Portal the menu to <body> so it's not clipped by any
                        // overflow:hidden/scroll ancestor in the table.
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        onMenuScrollToBottom={handleMenuScrollToBottom}
                        onInputChange={handleInputChange}
                        isLoading={isLoadingMore}
                        components={{ IndicatorSeparator: () => null }}
                        styles={{
                            control: (provided: any) => ({
                                ...provided,
                                minHeight: '32px',
                                fontSize: '0.875rem',
                                backgroundColor: 'var(--ds-surface)',
                                borderColor: 'var(--ds-edge-strong)',
                                '&:hover': { borderColor: 'var(--ds-ink-tertiary)' }
                            }),
                            option: (provided: any, state: any) => ({
                                ...provided,
                                fontSize: '0.875rem',
                                textAlign: 'left',
                                backgroundColor: state.isFocused ? 'var(--ds-accent-tint)' : 'var(--ds-surface)',
                                color: 'var(--ds-ink)'
                            }),
                            menu: (provided: any) => ({ ...provided, zIndex: 9999, backgroundColor: 'var(--ds-surface)' }),
                                singleValue: (provided: any) => ({ ...provided, color: 'var(--ds-ink)' }),
                                input: (provided: any) => ({ ...provided, color: 'var(--ds-ink)' }),
                            menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
                            indicatorsContainer: (provided: any) => ({ ...provided, height: '32px' }),
                            valueContainer: (provided: any) => ({ ...provided, padding: '0 8px' })
                        }}
                        data-testid={`attribute-value-select-${category.id}-${pairIndex}`}
                    />
                ) : (
                    <span className="tw-text-sm tw-text-ink tw-truncate tw-block tw-max-w-full" title={pair.attributeValue || ''} data-testid={`attribute-value-text-${category.id}-${pairIndex}`}>
                        {pair.attributeValue || '--'}
                    </span>
                )}
            </div>

            {/* Vertical line segment connecting from previous item */}
            {pairIndex > 0 && (
                <div className="tw-absolute tw-w-[2px] tw-bg-edge-strong tw-right-[-16px] tw--top-3.5 tw-h-[calc(50%+14px)]" />
            )}

            {/* Horizontal branch for first item (both sides) */}
            {count > 1 && pairIndex === 0 && (
                <div className="tw-absolute tw-h-[2px] tw-bg-edge-strong tw--right-[22px] tw-top-1/2 tw--translate-y-1/2 tw-w-2 tw-rounded-r-full" />
            )}

            {/* Horizontal branch for other items (left side only from the vertical line) */}
            {count > 1 && (
                <div className="tw-absolute tw-h-[2px] tw-bg-edge-strong tw-right-[-16px] tw-top-1/2 tw--translate-y-1/2 tw-w-2 tw-rounded-l-full" />
            )}

            {/* Vertical line extending down to next item */}
            {pairIndex < category.attribute.length - 1 && (
                <div className="tw-absolute tw-w-[2px] tw-bg-edge-strong tw-right-[-16px] tw-top-1/2 tw-h-[calc(50%+14px)]" />
            )}
        </div>
    );
};